/** Tipos compartilhados com a API do Face a Face V2. */

export type Role =
  | 'ADMIN_GERAL'
  | 'PASTOR'
  | 'COORDENADOR_GERAL'
  | 'GOVERNADOR'
  | 'MINISTRADOR';

export type UserStatus = 'ATIVO' | 'INATIVO' | 'SUSPENSO';

export interface User {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: Role;
  status: UserStatus;
  createdAt: string;
  updatedAt: string;
}

export type VisibilityScope = 'global' | 'own';

export interface PermissionProfile {
  role: Role;
  label: string;
  visibility: VisibilityScope;
  templates: 'manage' | 'read' | 'none';
  editions: 'manage' | 'create' | 'read' | 'none';
  repository: 'manage' | 'active' | 'read' | 'own' | 'none';
  userManagement: 'all' | 'all_except_admin' | 'leaders' | 'none';
  canIncorporateOfficialModel: boolean;
  immunity: boolean;
}

export type EncounterKind =
  | 'HOMENS'
  | 'MULHERES'
  | 'CASADOS'
  | 'JOVENS'
  | 'SOLTEIROS'
  | 'OUTRO';

export interface Template {
  id: string;
  name: string;
  kind: EncounterKind;
  description: string | null;
  isOfficial: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  ministrationsCount?: number;
}

export interface TemplateMinistration {
  id: string;
  templateId: string;
  position: number;
  /** Dia do encontro ao qual a ministração pertence (1..N). */
  dayNumber: number;
  title: string;
  theme: string | null;
  suggestedDurationMin: number;
  /** Intervalo livre após a ministração (transição, coffee break, altar). */
  intervalMin: number;
  /** Roteiro em texto puro. */
  script: string;
  /** Roteiro formatado (HTML do editor ABNT). */
  scriptHtml: string | null;
  createdAt: string;
  updatedAt: string;
}

export type EditionStatus = 'PLANEJADA' | 'ATIVA' | 'ENCERRADA' | 'CANCELADA';
export type ReadinessStatus = 'PENDENTE' | 'EM_PREPARACAO' | 'HOMOLOGADO';

export interface Edition {
  id: string;
  templateId: string;
  name: string;
  location: string | null;
  startDate: string;
  status: EditionStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  /** Dias do encontro, com horário de início próprio. */
  days?: EditionDay[];
}

/** Um dia do encontro em uma edição real. */
export interface EditionDay {
  id: string;
  editionId: string;
  dayNumber: number;
  startAt: string;
  label: string | null;
  createdAt: string;
  updatedAt: string;
}

export type MediaKind =
  | 'ROTEIRO'
  | 'PDF'
  | 'SLIDES'
  | 'AUDIO'
  | 'VIDEO'
  | 'IMAGEM'
  | 'PLAYLIST';

export type MediaProvider = 'LOCAL' | 'YOUTUBE' | 'SPOTIFY' | 'EXTERNO';
export type MediaScope = 'TEMPLATE' | 'TEMPLATE_MINISTRATION' | 'EDITION_MINISTRATION';

export interface MediaAsset {
  id: string;
  scope: MediaScope;
  ownerId: string;
  editionId: string | null;
  kind: MediaKind;
  title: string;
  provider: MediaProvider;
  externalUrl: string | null;
  storagePath: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  version: number;
  isOfficial: boolean;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
  fileUrl: string | null;
  embedUrl: string | null;
}

export interface EditionMinistration {
  id: string;
  editionId: string;
  templateMinistrationId: string | null;
  position: number;
  /** Dia do encontro ao qual a ministração pertence (1..N). */
  dayNumber: number;
  title: string;
  theme: string | null;
  durationMin: number;
  /** Intervalo livre após a ministração (não conta como tempo de fala). */
  intervalMin: number;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  ministradorId: string | null;
  readiness: ReadinessStatus;
  notes: string | null;
  customScript: string | null;
  customScriptHtml: string | null;
  createdAt: string;
  updatedAt: string;
  ministradorName: string | null;
  editionName: string | null;
  media: MediaAsset[];
  mediaCount: number;
}

export interface ScheduleSlot {
  id: string;
  position: number;
  dayNumber: number;
  title: string;
  durationMin: number;
  intervalMin: number;
  scheduledStart: string;
  scheduledEnd: string;
  nextStartsAt: string;
}

/** Resumo de um dia da agenda. */
export interface ScheduleDay {
  dayNumber: number;
  label: string | null;
  startAt: string;
  endsAt: string | null;
  itemCount: number;
  speakingMinutes: number;
  intervalMinutes: number;
  totalMinutes: number;
}

export interface ScheduleResult {
  slots: ScheduleSlot[];
  days: ScheduleDay[];
  totalMinutes: number;
  speakingMinutes: number;
  intervalMinutes: number;
  startsAt: string;
  endsAt: string;
}

/** Item da agenda pronta para exibição e impressão. */
export interface AgendaItem {
  id: string;
  position: number;
  dayNumber: number;
  title: string;
  theme: string | null;
  ministradorName: string | null;
  readiness: ReadinessStatus;
  durationMin: number;
  intervalMin: number;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  nextStartsAt: string | null;
  hasScript: boolean;
  mediaCount: number;
}

/** Agenda de um dia do encontro. */
export interface AgendaDay {
  dayNumber: number;
  label: string | null;
  startAt: string;
  endsAt: string | null;
  items: AgendaItem[];
  totals: {
    ministrations: number;
    speakingMinutes: number;
    intervalMinutes: number;
    totalMinutes: number;
    homologadas: number;
    pendentes: number;
  };
}

export interface AgendaView {
  edition: Edition;
  /** Agendas separadas por dia (encontros de vários dias). */
  days: AgendaDay[];
  items: AgendaItem[];
  totals: {
    days: number;
    ministrations: number;
    speakingMinutes: number;
    intervalMinutes: number;
    totalMinutes: number;
    homologadas: number;
    pendentes: number;
  };
  startsAt: string | null;
  endsAt: string | null;
}

export interface ChecklistItem {
  id: string;
  position: number;
  dayNumber: number;
  title: string;
  ministradorId: string | null;
  ministradorName: string | null;
  readiness: ReadinessStatus;
  mediaCount: number;
  hasScript: boolean;
  scheduledStart: string | null;
  overrunRisk: boolean;
}

export interface ChecklistView {
  edition: Edition;
  columns: Record<ReadinessStatus, ChecklistItem[]>;
  totals: {
    total: number;
    pendente: number;
    emPreparacao: number;
    homologado: number;
    progressPercent: number;
  };
  schedule: { startsAt: string | null; endsAt: string | null; totalMinutes: number; days: number };
}

export interface PulpitView {
  edition: Edition;
  current: EditionMinistration;
  previous: { id: string; title: string } | null;
  next: { id: string; title: string } | null;
  queue: Array<{ id: string; position: number; title: string; durationMin: number }>;
  /** Roteiro em texto puro (fallback). */
  script: string;
  /** Roteiro formatado (HTML sanitizado pela API). */
  scriptHtml: string | null;
  timer: {
    remainingSeconds: number;
    elapsedSeconds: number;
    overrun: boolean;
    serverTime: string;
  };
}

export interface SupportItem extends EditionMinistration {
  isCurrent: boolean;
  isNext: boolean;
}

export interface SupportQueue {
  edition: Edition;
  items: SupportItem[];
}

export interface AuditLog {
  id: string;
  actorId: string | null;
  actorName: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  summary: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface Session {
  token: string;
  user: User;
  permissions: PermissionProfile;
}

export const ROLE_LABELS: Record<Role, string> = {
  ADMIN_GERAL: 'Administrador Geral',
  PASTOR: 'Pastor',
  COORDENADOR_GERAL: 'Coordenador Geral',
  GOVERNADOR: 'Governador',
  MINISTRADOR: 'Ministrador',
};

export const READINESS_LABELS: Record<ReadinessStatus, string> = {
  PENDENTE: 'Pendente',
  EM_PREPARACAO: 'Em Preparação',
  HOMOLOGADO: 'Homologado / Pronto',
};

export const EDITION_STATUS_LABELS: Record<EditionStatus, string> = {
  PLANEJADA: 'Planejada',
  ATIVA: 'Ativa',
  ENCERRADA: 'Encerrada',
  CANCELADA: 'Cancelada',
};

export const MEDIA_KIND_LABELS: Record<MediaKind, string> = {
  ROTEIRO: 'Roteiro',
  PDF: 'PDF',
  SLIDES: 'Slides',
  AUDIO: 'Áudio',
  VIDEO: 'Vídeo',
  IMAGEM: 'Imagem',
  PLAYLIST: 'Playlist',
};

export const ENCOUNTER_KIND_LABELS: Record<EncounterKind, string> = {
  HOMENS: 'Homens',
  MULHERES: 'Mulheres',
  CASADOS: 'Casados',
  JOVENS: 'Jovens',
  SOLTEIROS: 'Solteiros',
  OUTRO: 'Outro',
};
